// packages/console/src/components/panels/LiveEncoderPanel.tsx
//
// Transcode-ladder table for the LIVE console, ported from the "Transcode
// ladder" block of docs/design/ui_kits/aether-live/Transcoder.jsx. Each rung is
// a REAL LiveRung off the SSE stream: per-rung enable toggle, name, codec,
// profile, target bitrate, measured bitrate with a Meter, GPU share, and encode
// state. Audio is a fixed final row.
//
// Selecting a rung drives the Rendition params panel on the right. The enable
// toggle is a control (onToggle) the parent applies; measured/GPU/state are
// read-only from the stream. Honest absence (R10): an off/idle rung shows an
// em-dash for measured and GPU, and a stopped session leaves the whole table at
// its idle state rather than fabricating encode figures.

import { Panel, Dot, Meter, TChip } from "../Parts";
import { Icon } from "../Icons";
import type { LiveRung } from "../../api/live";
import { EM_DASH, fmtMbps, fmtPct, mbps } from "../../lib/format";

const COLS = "38px 96px 78px 88px 92px 1fr 74px 70px";

function rungStateLabel(r: LiveRung): { tone: "ok" | "idle"; label: string } {
  if (!r.on) return { tone: "idle", label: "Off" };
  if (r.state === "encoding") return { tone: "ok", label: "Encoding" };
  return { tone: "idle", label: "Idle" };
}

export function LiveEncoderPanel({
  rungs,
  sel,
  onSelect,
  onToggle,
  onAir,
}: {
  rungs: LiveRung[];
  sel: string | null;
  onSelect: (rungName: string) => void;
  onToggle: (rungName: string, on: boolean) => void;
  onAir: boolean;
}) {
  return (
    <Panel
      title="Transcode ladder"
      bodyStyle={{ padding: 0, flex: 1, overflowY: "auto" }}
      style={{ flex: 1, minHeight: 0 }}
      right={
        <div style={{ display: "flex", gap: 6 }}>
          <TChip active>ABR ladder</TChip>
          <TChip>Passthrough</TChip>
        </div>
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: COLS,
          alignItems: "center",
          padding: "0 13px",
          height: 32,
          borderBottom: "1px solid var(--line)",
          font: "500 9px var(--font-sans)",
          letterSpacing: ".13em",
          textTransform: "uppercase",
          color: "var(--fg4)",
          position: "sticky",
          top: 0,
          background: "var(--bg-panel)",
          zIndex: 2,
        }}
      >
        <span>On</span>
        <span>Rendition</span>
        <span>Codec</span>
        <span>Profile</span>
        <span>Target</span>
        <span>Measured</span>
        <span>GPU</span>
        <span>State</span>
      </div>
      {rungs.length === 0 ? (
        <div style={{ padding: "16px 13px", font: "var(--t-micro)", color: "var(--fg4)" }}>
          {onAir ? "No ladder reported" : "Ladder idle"}
        </div>
      ) : (
        rungs.map((r) => {
          const on = r.on;
          const measured = mbps(r.measuredBitsPerSec);
          const target = mbps(r.targetBitsPerSec);
          const pct = on && measured !== null && target && target > 0 ? (measured / target) * 100 : 0;
          const st = rungStateLabel(r);
          return (
            <div
              key={r.rungName}
              onClick={() => onSelect(r.rungName)}
              style={{
                display: "grid",
                gridTemplateColumns: COLS,
                alignItems: "center",
                padding: "0 13px",
                height: 44,
                cursor: "pointer",
                borderBottom: "1px solid var(--line)",
                background: sel === r.rungName ? "var(--blue-tint)" : "transparent",
                boxShadow: sel === r.rungName ? "inset 2px 0 0 var(--blue-500)" : "none",
                opacity: on ? 1 : 0.45,
              }}
            >
              <button
                className="ae-b"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(r.rungName, !on);
                }}
                aria-pressed={on}
                aria-label={`${r.rungName} enable`}
                style={{
                  width: 26,
                  height: 15,
                  borderRadius: 999,
                  cursor: "pointer",
                  position: "relative",
                  padding: 0,
                  background: on ? "var(--blue-500)" : "var(--bg-hover)",
                  border: "none",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 2,
                    left: on ? 13 : 2,
                    width: 11,
                    height: 11,
                    borderRadius: "50%",
                    background: "#fff",
                    transition: "left var(--dur-fast) var(--ease-std)",
                  }}
                />
              </button>
              <span style={{ font: "500 13px var(--font-mono)", color: "#fff" }}>{r.rungName}</span>
              <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)" }}>{r.codec || EM_DASH}</span>
              <span style={{ font: "var(--t-body-sm)", color: "var(--fg3)" }}>{r.profile || EM_DASH}</span>
              <span
                style={{
                  font: "400 12px var(--font-mono)",
                  color: "var(--fg2)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {target !== null ? `${fmtMbps(r.targetBitsPerSec, 1)} Mb/s` : EM_DASH}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 10, paddingRight: 16 }}>
                <span
                  style={{
                    font: "400 12px var(--font-mono)",
                    color: on && measured !== null ? "#fff" : "var(--fg4)",
                    width: 46,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {on && measured !== null ? fmtMbps(r.measuredBitsPerSec) : EM_DASH}
                </span>
                <div style={{ flex: 1 }}>
                  <Meter pct={pct} color={on ? "var(--blue-500)" : "var(--idle)"} />
                </div>
              </div>
              <span
                style={{
                  font: "400 12px var(--font-mono)",
                  color: "var(--fg2)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {on && r.gpuSharePct !== null ? fmtPct(r.gpuSharePct) : EM_DASH}
              </span>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  font: "var(--t-micro)",
                  color: "var(--fg2)",
                }}
              >
                <Dot tone={st.tone} />
                {st.label}
              </span>
            </div>
          );
        })
      )}
      {/* Audio: a fixed final row (ported from the prototype). Its encode state
          follows the session (encoding on air, else idle). */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "38px 96px 78px 1fr 70px",
          alignItems: "center",
          padding: "0 13px",
          height: 44,
          borderBottom: "1px solid var(--line)",
        }}
      >
        <Icon name="audio-lines" size={14} color="var(--viz-3)" />
        <span style={{ font: "500 13px var(--font-mono)", color: "#fff" }}>Audio</span>
        <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)" }}>AAC-LC</span>
        <span style={{ font: "400 12px var(--font-mono)", color: "var(--fg3)" }}>128 kb/s · 48 kHz · stereo</span>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            font: "var(--t-micro)",
            color: "var(--fg2)",
          }}
        >
          <Dot tone={onAir ? "ok" : "idle"} />
          {onAir ? "Encoding" : "Idle"}
        </span>
      </div>
    </Panel>
  );
}
